// @ts-nocheck
import React, { useState, useContext, useEffect } from 'react';
import Router from 'next/router';
import SCInput from '@/components/sc-controls/form-controls/sc-input';
import SCPasswordInput from '@/components/sc-controls/form-controls/sc-password-input';
import { colors, layout} from '@/theme';
import Fetch from '@/utils/Fetch';
import ToastContext from '@/utils/toast-context';
import SettingsMenu from '@/components/settings/menu';
import Helper from '@/utils/helper';
import * as Enums from '@/utils/enums';
import ConfirmAction from '@/components/modals/confirm-action';
import SubscriptionContext from '@/utils/subscription-context';
import BillingService from '@/services/billing-service';
import Constants from '@/utils/constants';
import SCComboBox from "@/components/sc-controls/form-controls/sc-combobox";
import employeeService from '@/services/employee/employee-service';
import useEmployeeAvailability from '@/hooks/useEmployeeAvailability';
import { IconInfoCircle } from '@tabler/icons-react';
import { Button as MantineButton } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import Storage from '@/utils/storage';
import { createFileRoute } from '@tanstack/react-router'



function CreateEmployee() {

  const subscriptionContext = useContext(SubscriptionContext);
  const isTrial = subscriptionContext?.subscriptionInfo?.AccessStatus === Enums.AccessStatus.Trial;

  let currPath = '';
  if (Router.router) {
    currPath = Router.router.asPath;
  }

  const [submitting, setSubmitting] = useState(false);
  const toast = useContext(ToastContext);

  const [formIsDirty, setFormIsDirty] = useState(false);
  const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());

  const [selectedPermissions, setSelectedPermissions] = useState<any[]>([]);
  const [primaryRoles, setPrimaryRoles] = useState<any[]>([]);

  const [firstLoad, setFirstLoad] = useState(true);

  useEffect(() => {
    if (!firstLoad && selectedPermissions) {
      setFormIsDirty(true);
    }
    setFirstLoad(false);
  }, [selectedPermissions]);

  const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);
  const [customerStatus, setCustomerStatus] = useState<any>(null);

  const getSubcriptionInfo = async () => {
    const [subscriptionInfo, message] = await BillingService.getSubcriptionInfo(subscriptionContext);
    if (subscriptionInfo) {
      setAccessStatus(subscriptionInfo.AccessStatus);
      setCustomerStatus(subscriptionInfo.CustomerStatus);
    }
  };

  const getPrimaryRoles = async () => {
    const roles = (await employeeService.getPrimaryRoles()) || [];
    setPrimaryRoles(roles.filter((role: any) => ![role, role?.value, role?.Value, role?.label, role?.Label, role?.Name].includes('Other')));
  };

  useEffect(() => {
    getSubcriptionInfo();
    getPrimaryRoles();
  }, []);

  Helper.preventRouteChange(formIsDirty, setFormIsDirty, setConfirmOptions, submitEmployee);

  const [inputs, setInputs] = useState({
    FirstName: "",
    LastName: "",
    EmailAddress: "",
    MobileNumber: "",
    canLogin: true, //subscriptionContext && subscriptionContext.subscriptionInfo && subscriptionContext.subscriptionInfo.AccessStatus === Enums.AccessStatus.Trial ? true : false,
    password: "",
    confirmPassword: "",
    IsTechnician: false,
    IsSalesRepresentative: false,
    SendEmail: true,
    SendSMS: true,
    SendNotifications: true,
    DisplayColor: "",
    IsAllReport: true,
    IsAllJobCardStatus: true,
    SessionTimeOut: 0,
    PrimaryRole: "",
    primaryRoleText: ''
  });

  const [inputErrors, setInputErrors] = useState<any>({});
  const [availabilityErrors, setAvailabilityErrors] = useState<any>({});

  const { queueCheck, cancelCheck } = useEmployeeAvailability((token: string, field: string, message: string | null) => {
    setAvailabilityErrors((previous: any) => ({ ...previous, [field]: message || undefined }));
  });

  const handleInputChange = (e: any) => {
    const setter = {
      ...inputs,
      [e.name]: e.value
    }

    if (e.name === 'primaryRoleText') {
      setter.PrimaryRole = e.value
    }

    setInputs(setter);

    setInputErrors((previous: any) => (previous[e.name] ? { ...previous, [e.name]: undefined } : previous));
    setAvailabilityErrors((previous: any) => (previous[e.name] ? { ...previous, [e.name]: undefined } : previous));

    setFormIsDirty(true);
  }

  function buildValidationItems() {
    let validationItems = [
      { key: 'FirstName', value: inputs.FirstName, required: !inputs.canLogin, type: Enums.ControlType.Text },
      { key: 'LastName', value: inputs.LastName, required: false, type: Enums.ControlType.Text },
      { key: 'EmailAddress', value: inputs.EmailAddress, required: true, type: Enums.ControlType.Email, rejectServCraft: true },
      { key: 'MobileNumber', value: inputs.MobileNumber, required: true, type: Enums.ControlType.ContactNumber },
      { key: 'PrimaryRole', value: inputs.PrimaryRole, required: true, type: Enums.ControlType.Text },
    ];

    if (inputs.canLogin === true) {
      validationItems.push({
        key: 'SessionTimeOut', value: inputs.SessionTimeOut, required: true, type: Enums.ControlType.Number, lte: 10080, gte: 0
      });
    }

    return validationItems;
  }

  const handleFieldBlur = (field: string) => {
    const validationItem = buildValidationItems().find((x: any) => x.key === field);
    if (!validationItem) return;

    const { errors } = Helper.validateInputs([validationItem]);

    setAvailabilityErrors((previous: any) => ({ ...previous, [field]: undefined }));

    if (errors[field]) {
      cancelCheck(field);
      setInputErrors((previous: any) => ({ ...previous, [field]: errors[field] }));
      return;
    }

    setInputErrors((previous: any) => ({ ...previous, [field]: undefined }));
    if (field === 'EmailAddress' || field === 'MobileNumber') {
      queueCheck(field, field, inputs[field], null);
    }
  };

  async function submitEmployee() {
    setSubmitting(true);

    const validationItems = buildValidationItems();

    let { isValid, errors } = Helper.validateInputs(validationItems);

    if (errors["SessionTimeOut"]) {
      errors["SessionTimeOut"] = "Must be less than or equal to 7 days";
    }

    if (inputs.canLogin === true && inputs.SessionTimeOut < Constants.minimumSessionTimeoutMinutes && inputs.SessionTimeOut > 0) {
      errors["SessionTimeOut"] = `Must be greater than ${Constants.minimumSessionTimeoutMinutes} minutes or leave as 0 for infinite`;
      isValid = false;
    }

    ['EmailAddress', 'MobileNumber'].forEach((field) => {
      if (availabilityErrors[field]) {
        errors[field] = availabilityErrors[field];
        isValid = false;
      }
    });

    setInputErrors(errors);

    if (isValid) {

      let isAllReport = selectedPermissions && selectedPermissions.findIndex((x: any) => x === "Employee.IsAllReport") > -1;

      let employeeParams = {
        FirstName: inputs.FirstName,
        LastName: inputs.LastName,
        EmailAddress: inputs.EmailAddress,
        MobileNumber: inputs.MobileNumber,
        SendEmail: inputs.SendEmail,
        SendSMS: inputs.SendSMS,
        SendNotifications: inputs.SendNotifications,
        IsSalesRepresentative: inputs.IsSalesRepresentative,
        IsTechnician: inputs.IsTechnician,
        IsActive: true,
        DisplayColor: inputs.DisplayColor,
        IsAllReport: isAllReport,
        IsAllJobCardStatus: inputs.IsAllJobCardStatus,
        PrimaryRole: inputs.PrimaryRole,
      }
      let params = {
        Employee: employeeParams,
        PermissionList: selectedPermissions
      };

      if (inputs.canLogin) {
        params = {
          ...params, User: {
            UserName: inputs.EmailAddress,
            IsActive: true,
            SessionTimeOut: inputs.SessionTimeOut
          }
        };
      }

      const employeePostResponse = await Fetch.post({
        url: '/Employee',
        params: params
      });
      if (employeePostResponse.ID) {
        Helper.mixpanelTrack(Constants.mixPanelEvents.createEmployee, {
          "employeeID": employeePostResponse.ID
        });
        setFormIsDirty(false);
        if (employeePostResponse.InviteLink || employeePostResponse.InviteWhatsAppUrl) {
          try {
            sessionStorage.setItem('sc:inviteShare', JSON.stringify({
              link: employeePostResponse.InviteLink,
              whatsAppUrl: employeePostResponse.InviteWhatsAppUrl,
              name: `${inputs.FirstName || ''} ${inputs.LastName || ''}`.trim() || inputs.EmailAddress,
              email: inputs.EmailAddress,
              mobile: inputs.MobileNumber
            }));
          } catch { /* ignore quota */ }
        }
        await Helper.waitABit();
        toast.setToast!!!({
          message: 'Employee invited successfully',
          show: true,
          type: 'success'
        });
        Helper.nextRouter(Router.push, `/settings/employee/${employeePostResponse.ID}?created=true`);
      } else if (employeePostResponse?.Message) {
        const msg = employeePostResponse.Message;
        if (/email/i.test(msg)) {
          // Server doesn't return a field name for this error - the message text always
          // references the email address, so that's used to route it to the field itself
          // rather than a generic toast.
          setInputErrors({ ...inputErrors, EmailAddress: msg });
          showNotification({ color: 'yellow.7', message: msg, autoClose: 5000 });
        } else if (/mobile/i.test(msg)) {
          setInputErrors({ ...inputErrors, MobileNumber: msg });
          showNotification({ color: 'yellow.7', message: msg, autoClose: 5000 });
        } else {
          showNotification({ color: 'yellow.7', message: msg, autoClose: 5000 });
        }
        setSubmitting(false);
      } else if (employeePostResponse?.message || employeePostResponse?.serverMessage) {
        const msg = employeePostResponse.message || employeePostResponse.serverMessage;
        showNotification({ color: 'yellow.7', message: msg, autoClose: 4000 });
        setSubmitting(false);
      } else {
        showNotification({ color: 'yellow.7', message: 'Something went wrong', autoClose: 4000 });
        setSubmitting(false);
      }
    } else {
      toast.setToast!!!({
        message: 'There are errors on the page.',
        show: true,
        type: 'error'
      });
      setSubmitting(false);
    }

    return isValid;
  }

  const setColour = (colour: any) => {
    setInputs({ ...inputs, DisplayColor: colour });
    setFormIsDirty(true);
  }

  const handleCanLoginChange = (canLogin: boolean) => {
    handleInputChange({ name: 'canLogin', value: canLogin });
    setInputErrors((previous: any) => (previous.FirstName ? { ...previous, FirstName: undefined } : previous));
  };

  const inviting = inputs.canLogin;

  const cancel = () => {
    Helper.nextRouter(Router.push, '/settings/employee/list');
  };

  return (
    <div className="settings-container">
      {/*<div className="row">
        <div className="title">
          <Breadcrumbs
            crumbs={[
              { text: 'Settings', link: '/settings', suppressLink: true },
              { text: 'Employees', link: '/settings/employee/list' },
              { text: 'Create Employee', link: '/settings/employee/create' },
            ]}
          />
        </div>
      </div>*/}
      <div className="row">
        <SettingsMenu urlPath={currPath} />
        <div className="settings-card">
          <div className="split">
            <div>
              <h3>Employee Details</h3>
              <div className="row">
                <div className="column">
                  <SCInput
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('EmailAddress')}
                    error={inputErrors.EmailAddress || availabilityErrors.EmailAddress}
                    label={"Email address (this will be the employee's username)"}
                    hint={inviting ? "They'll sign in with this email." : "We'll send job updates here."}
                    name="EmailAddress"
                    required={true}
                    value={inputs.EmailAddress}
                    cypress="data-cy-email"
                    placeholder="email@address.com"
                  />
                </div>
              </div>
              <div className="row">
                <div className="column">
                  <SCInput
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('MobileNumber')}
                    error={inputErrors.MobileNumber || availabilityErrors.MobileNumber}
                    label={"Mobile number"}
                    hint={inviting ? "We'll SMS their invite link here too." : "We'll SMS job updates here."}
                    name="MobileNumber"
                    type="tel"
                    required={true}
                    value={inputs.MobileNumber}
                    cypress="data-cy-mobile"
                  />
                </div>
              </div>
              <div className="row">
                <div className="column">
                  <SCInput
                    onChange={handleInputChange}
                    error={inputErrors.FirstName}
                    onBlur={() => handleFieldBlur('FirstName')}
                    label={inviting ? "First name (optional)" : "First name"}
                    name="FirstName"
                    required={!inviting}
                    value={inputs.FirstName}
                    hint={inviting
                      ? "They can add their name when they first sign in."
                      : "We need a name because they won't sign in to add it. It shows on jobs and in your employee list."}
                    cypress="data-cy-firstname"
                  />
                </div>
              </div>
              <div className="row">
                <div className="column">
                  <SCInput
                    onChange={handleInputChange}
                    error={inputErrors.LastName}
                    label={"Last name (optional)"}
                    name="LastName"
                    required={false}
                    value={inputs.LastName}
                    cypress="data-cy-lastname"
                  />
                </div>
              </div>
              <div className="row">
                <div className="column">
                  <SCComboBox
                    canSearch={false}
                    onChange={(value: any) => handleInputChange({ name: 'primaryRoleText', value })}
                    error={inputErrors.PrimaryRole}
                    label={'Primary role'}
                    hint={inviting ? "Sets what they can see and do in ServCraft." : "Used when you assign them to jobs."}
                    name="primaryRoleText"
                    required
                    options={primaryRoles}
                    value={inputs.primaryRoleText}
                  />
                </div>
              </div>

              {(accessStatus !== Enums.AccessStatus.LockedWithOutAccess && accessStatus !== Enums.AccessStatus.LockedWithAccess)
                || (customerStatus === "Trial") ?
                <>
                  <div className="sign-in-section">
                    <h4 id="sign-in-heading">Will they sign in to ServCraft?</h4>
                    <div className="sign-in-options" role="radiogroup" aria-labelledby="sign-in-heading">
                      <label className={`sign-in-card ${inviting ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="signIn"
                          value="invite"
                          checked={inviting}
                          onChange={() => handleCanLoginChange(true)}
                        />
                        <div>
                          <b>Yes, invite them</b>
                          <span>We'll email and SMS a link so they can set their own password. The link lasts 7 days.</span>
                        </div>
                      </label>
                      <label className={`sign-in-card ${inviting ? '' : 'selected'}`}>
                        <input
                          type="radio"
                          name="signIn"
                          value="job-updates"
                          checked={!inviting}
                          onChange={() => handleCanLoginChange(false)}
                        />
                        <div>
                          <b>No, job updates only</b>
                          <span>They get job updates by email and SMS but can't sign in. You can invite them later.</span>
                        </div>
                      </label>
                    </div>
                  </div>
                </>
                : ''}

              {/* <h3>Communication</h3>
              <SCCheckbox
                onChange={() => handleInputChange({ name: 'SendEmail', value: !inputs.SendEmail })}
                value={inputs.SendEmail}
                label="Receive Email Notifications"
                cypress="data-cy-sendemail"
              />
              <SCCheckbox
                onChange={() => handleInputChange({ name: 'SendSMS', value: !inputs.SendSMS })}
                value={inputs.SendSMS}
                label="Receive SMS Notifications"
                cypress="data-cy-sendsms"
              />
              <SCCheckbox
                onChange={() => handleInputChange({ name: 'SendNotifications', value: !inputs.SendNotifications })}
                value={inputs.SendNotifications}
                label="Receive Push Notifications"
                cypress="data-cy-sendnotifications"
              /> */}

              {/* {inputs.canLogin ?
                <>
                  <h3>Permissions</h3>
                  <EditPermissions selectedPermissions={selectedPermissions} setSelectedPermissions={setSelectedPermissions} />
                </> : ''
              } */}

              {/* <h3>Colour</h3>

              <ColourPicker selectedColour={inputs.DisplayColor} setColour={setColour} /> */}

              <div className="footer-info">
                <div className="footer-info-line">
                  <IconInfoCircle size={18} aria-hidden="true" />
                  <span>
                    {inviting
                      ? "The invite goes out as soon as you add them. You can copy the link or share it on WhatsApp afterwards."
                      : "They won't get a login. You can invite them from their employee page at any time."}
                  </span>
                </div>
                {inviting && !isTrial && (
                  <div className="licence-line">
                    This adds 1 user licence to your bill from today, even if they haven't joined yet.
                  </div>
                )}
              </div>

              <div className="actions">
                <MantineButton disabled={submitting} onClick={submitEmployee} ml={"0.5rem"} >
                  {submitting ? "Sending invite" : (inviting ? "Add and send invite" : "Add employee")}
                </MantineButton>
                {/* <Button text={submitting ? "Creating" : "Create"} disabled={submitting} extraClasses="auto" onClick={submitEmployee} /> */}
                <MantineButton disabled={submitting} onClick={cancel} variant={"outline"}>
                  Cancel
                </MantineButton>
                {/* <Button text="Cancel" extraClasses="auto hollow" disabled={submitting} onClick={cancel} /> */}
              </div>

            </div>
            {/* <div className="empty">
              <img src="/customer-list.svg" alt="Job Folder" />
              <h3>Create new employee</h3>
              <p>Add new technicians or <br /> administrators quick and easy</p>
            </div> */}
          </div>
        </div>
      </div>

      <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />

      <style jsx>{`
        .add-location {
          align-items: center;
          color: ${colors.bluePrimary};
          cursor: pointer;
          display: flex;
          font-weight: bold;
        }
        .add-location img {
          margin: 2px 0.5rem 0 0;
        }
        .container {
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          height: 100%;
          padding: 1.5rem 3rem;
          overflow-x: hidden;
        }
        .column {
          display: flex;
          flex-direction: column;
          width: ${layout.inputWidth};
        }
        .column-margin {
          margin-left: 24px;
        }
        .button-container {
          flex-shrink: 0;
          width: 10rem;
        }
        .button-container :global(.button){
          margin-top: 0.5rem;
        }
        .title {
          align-items: center;
          color: ${colors.darkPrimary};
          display: flex;
          height: 2.5rem;
          width: 100%;
        }
        .title h1 {
          font-size: 32px;
          margin: 6px 0 2rem 0;
        }
        .title p {
          font-size: 14px;
          margin: 0;
        }
        .row {
          display: flex;
          justify-content: space-between;
        }
        .row-align {
          align-items: center;
          height: 1.75rem
        }
        .empty {
          align-items: center;
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          height: fit-content;
          justify-content: center;
          margin-left: 1.5rem;
          width: 520px;
        }
        .empty img {
          height: 110px;
          margin-bottom: 1rem;
        }
        .empty h3 {
          color: ${colors.darkSecondary};
          font-size: 16px;
          margin: 0 0 0.75rem;
        }
        .empty p {
          color: ${colors.blueGrey};
          margin: 0;
          text-align: center;
        }
        .actions {
            margin-top: 1rem;
            display: flex;
            flex-direction: row-reverse;
            width: ${layout.inputWidth};
        }
        .actions :global(.button){
            margin-left: 0.5rem;
            margin-top: 1rem;
            padding: 0 1rem;
            white-space: nowrap;
        }
        .sign-in-section {
          margin-top: 1.5rem;
          width: ${layout.inputWidth};
        }
        .sign-in-section h4 {
          color: ${colors.darkPrimary};
          font-size: 16px;
          margin: 0 0 0.75rem;
        }
        .sign-in-options {
          display: grid;
          gap: 0.75rem;
          grid-template-columns: 1fr 1fr;
        }
        .sign-in-card {
          align-items: flex-start;
          background: #fff;
          border: 1.5px solid #c4c8cc;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          gap: 0.75rem;
          padding: 1rem;
        }
        .sign-in-card.selected {
          background: #f4f7fe;
          border-color: ${colors.bluePrimary};
          box-shadow: 0 0 0 1px ${colors.bluePrimary};
        }
        .sign-in-card input {
          accent-color: ${colors.bluePrimary};
          flex: none;
          height: 18px;
          margin: 2px 0 0;
          width: 18px;
        }
        .sign-in-card b {
          color: ${colors.darkPrimary};
          display: block;
          font-size: 15px;
          margin-bottom: 4px;
        }
        .sign-in-card span {
          color: ${colors.blueGrey};
          display: block;
          font-size: 13px;
          line-height: 1.45;
        }
        .footer-info {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-top: 1.5rem;
          width: ${layout.inputWidth};
        }
        .footer-info-line {
          align-items: flex-start;
          color: ${colors.blueGrey};
          display: flex;
          font-size: 14px;
          gap: 0.5rem;
          line-height: 1.4;
        }
        .footer-info-line :global(svg) {
          flex: none;
          margin-top: 1px;
        }
        .licence-line {
          color: ${colors.darkPrimary};
          font-size: 13px;
          font-weight: 600;
          line-height: 1.4;
          padding-left: 26px;
        }
      `}</style>
    </div>
  );
}

export default CreateEmployee

export const Route = createFileRoute('/_authenticated/settings/employee/create')({ component: CreateEmployee })
